// [목적] /login 모바일 레이아웃 — docs/플리오랩/gangwonfolio.html LoginScreen 1:1 포팅
// [이유] 2026-05-16 reauth-redesign — 디자인 원본의 흰 배경 + AuthBackground 글로우 + LogoSlot 84 +
//         "강원을 그리다" 헤드라인 + 부카피 + OAuth 4종 + 약관 구성으로 복원.
//         이전 brandBeige 페이지 배경 + brandGold 포커스 링 + 둘러보기 CTA 구성 폐기.
// [사이드 이팩트]
//   - 페이지 배경: surface0(white). AuthBackground 가 좌상단/좌하단 글로우(블루/그린) + 산 SVG 담당.
//   - LogoSlot size=84, onDark=false — 모바일 흰 배경 위 brandBlue 박스 + 흰 "강" 글자.
//   - OAuthButtonStack: disabledProviders=[] (디자인 원본대로 4종 활성).
//     Apple OAuth BE 미구현 환경에서는 부모 LoginPage 가 핸들러에서 안내 처리 권장.
//   - 둘러보기 CTA 제거 (디자인 원본 미포함). LoginPage 시그니처에서 onAnonBrowse prop 제거 필요.
//   - 약관 링크 포커스 링: brandBlue (warm 시절 brandGold 폐기).
// [수정 시 고려사항]
//   - AUTH_COLORS / AUTH_SPACING / AUTH_BORDER 토큰 경유 필수. hex/px 직접 입력 0건.
//   - Tab 순서: OAuth 4종 → 이용약관 → 개인정보 처리방침.
//   - 약관 navigate 경로(/settings/tos, /settings/privacy)는 LoginDesktopLayout 과 동기화.
//   - maxWidth 480, p 56/28/32 디자인 원본 비례 유지.

import { Box, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { AuthBackground, LogoSlot } from './AuthChrome.js';
import { OAuthButtonStack, type OAuthProvider } from './OAuthButtonStack.js';
import { AUTH_BORDER, AUTH_COLORS, AUTH_SPACING } from './tokens.js';

export interface LoginMobileLayoutProps {
  /** OAuth 버튼 클릭 핸들러 — 부모(LoginPage)에서 주입 */
  onProviderClick: (provider: OAuthProvider) => void;
}

/**
 * [목적] /login 모바일 단일 컬럼 레이아웃 (viewport < 1024px)
 * [이유] 디자인 원본 LoginScreen — 흰 배경 + AuthBackground 글로우 + 중앙 로고/타이틀 + 버튼 스택 + 약관
 * [사이드 이팩트] minHeight 100vh + overflowY auto — 단축 스크롤 보장. AuthBackground 는 absolute 인셋
 * [수정 시 고려사항] 데스크톱 분기는 LoginPage 의 useIsDesktop 책임 — 본 컴포넌트는 항상 모바일 가정
 */
export function LoginMobileLayout({ onProviderClick }: LoginMobileLayoutProps): JSX.Element {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        overflowY: 'auto',
        bgcolor: AUTH_COLORS.surface0,
        position: 'relative',
      }}
    >
      <AuthBackground />
      <Box
        sx={{
          position: 'relative',
          p: AUTH_SPACING.cardPadding,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          maxWidth: AUTH_SPACING.cardMaxWidth,
          mx: 'auto',
        }}
      >
        {/* 로고 + 타이틀 + 부카피 */}
        <Box sx={{ textAlign: 'center', mb: AUTH_SPACING.subToStackMb }}>
          {/* 중앙 정렬 LogoSlot — Box wrapper 로 마진 보장 */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: AUTH_SPACING.logoBelowMb }}>
            <LogoSlot size={84} onDark={false} />
          </Box>
          <Typography
            component="h1"
            sx={{
              fontSize: 24,
              fontWeight: 800,
              color: AUTH_COLORS.textHigh,
              letterSpacing: '-0.6px',
              mb: AUTH_SPACING.headlineToSubMb,
              lineHeight: 1.2,
            }}
          >
            강원을 그리다
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: AUTH_COLORS.textMid, lineHeight: 1.55 }}>
            대화로 만드는 나만의 강원 여행 폴리오
            <br />
            지금 바로 시작해보세요
          </Typography>
        </Box>

        {/* OAuth 버튼 스택 — [수정: 디자인 개편] Apple은 OAuthButtonStack에서 완전히 제거되어 3종만 나온다. */}
        <Box sx={{ mb: 'auto' }}>
          <OAuthButtonStack onProviderClick={onProviderClick} fullWidth />
          {/* [수정: 디자인 개편] "로그인 없이 먼저 만들어보기" 게스트 진입 버튼 */}
          <Box
            component={RouterLink}
            to="/chat"
            aria-label="로그인 없이 먼저 만들어보기"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '48px',
              mt: AUTH_SPACING.stackGap,
              border: `1.5px solid ${AUTH_COLORS.dividerBase}`,
              borderRadius: AUTH_BORDER.buttonRadius,
              color: AUTH_COLORS.textHigh,
              fontSize: 13,
              fontWeight: 700,
              textDecoration: 'none',
              '&:focus-visible': {
                outline: `${AUTH_BORDER.focusRingWidth} solid ${AUTH_COLORS.brandBlue}`,
                outlineOffset: AUTH_BORDER.focusRingOffset,
              },
            }}
          >
            로그인 없이 먼저 만들어보기
          </Box>
        </Box>

        {/* 약관 안내 */}
        <Typography
          sx={{
            textAlign: 'center',
            mt: AUTH_SPACING.tosTopMt,
            fontSize: 10.5,
            color: AUTH_COLORS.textLow,
            lineHeight: 1.6,
          }}
        >
          가입 시{' '}
          {/* [수정: QA-P2-18/P3-01] span role="link" 대신 RouterLink 사용 — LoginDesktopLayout 과 동일하게 실제 <a> 시맨틱 부여 */}
          <Box
            component={RouterLink}
            to="/settings/tos"
            aria-label="이용약관 보기"
            sx={{
              color: AUTH_COLORS.dividerLink,
              fontWeight: 600,
              textDecoration: 'underline',
              cursor: 'pointer',
              '&:hover': { color: AUTH_COLORS.textHigh },
              '&:focus-visible': {
                outline: `${AUTH_BORDER.focusRingWidth} solid ${AUTH_COLORS.brandBlue}`,
                outlineOffset: AUTH_BORDER.focusRingOffset,
                borderRadius: '2px',
              },
            }}
          >
            이용약관
          </Box>{' '}
          및{' '}
          <Box
            component={RouterLink}
            to="/settings/privacy"
            aria-label="개인정보 처리방침 보기"
            sx={{
              color: AUTH_COLORS.dividerLink,
              fontWeight: 600,
              textDecoration: 'underline',
              cursor: 'pointer',
              '&:hover': { color: AUTH_COLORS.textHigh },
              '&:focus-visible': {
                outline: `${AUTH_BORDER.focusRingWidth} solid ${AUTH_COLORS.brandBlue}`,
                outlineOffset: AUTH_BORDER.focusRingOffset,
                borderRadius: '2px',
              },
            }}
          >
            개인정보 처리방침
          </Box>
          에<br />
          동의한 것으로 간주됩니다
        </Typography>
      </Box>
    </Box>
  );
}
